import type { CompiledChapter } from "./schema/compiled";

/** Sits beside the portraits and is published with them, but belongs to no character. */
export const BOOK_COVER_STEM = "book-cover";

const TOP_CAST = 8;

export interface BookSummary {
  bookId: string;
  title: string;
  chapters: { id: string; title: string; scenes: number; cast: string[] }[];
  scenes: number;
  characters: number;
  places: number;
  /** Earliest and latest dated moments in the story, as "YYYY-MM". */
  storyFrom: string | null;
  storyTo: string | null;
  /** The people in the most scenes, most first. */
  topCast: { id: string; displayName: string; scenes: number }[];
}

export function buildBookSummary(
  bookId: string,
  title: string,
  chapters: readonly CompiledChapter[],
): BookSummary {
  const scenes = chapters.flatMap((chapter) => chapter.scenes);
  const dates = scenes
    .flatMap((scene) => scene.narrativeTime?.approxStart ?? [])
    .map((start) => start.slice(0, 7))
    .sort();
  const names = new Map<string, string>();
  const placeIds = new Set<string>();
  for (const chapter of chapters) {
    for (const character of chapter.characters) {
      const id = character.flashbackOf ?? character.id;
      names.set(id, names.get(id) ?? character.displayName);
    }
    for (const place of chapter.places) {
      placeIds.add(place.id);
    }
  }
  const counts = new Map<string, number>();
  for (const scene of scenes) {
    const here = new Set(scene.participants
      .filter((item) => item.role === "present" || item.role === "pov")
      .map((item) => item.characterId.split("@")[0] ?? item.characterId));
    for (const id of here) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }
  return {
    bookId,
    title,
    chapters: chapters.map((chapter) => ({
      id: chapter.chapterId,
      title: chapter.title,
      scenes: chapter.scenes.length,
      cast: chapterCast(chapter),
    })),
    scenes: scenes.length,
    characters: names.size,
    places: placeIds.size,
    storyFrom: dates[0] ?? null,
    storyTo: dates.at(-1) ?? null,
    topCast: [...counts]
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, TOP_CAST)
      .map(([id, count]) => ({ id, displayName: names.get(id) ?? id, scenes: count })),
  };
}

/** Who is on stage in a chapter, most scenes first. Minor characters stay out. */
function chapterCast(chapter: CompiledChapter): string[] {
  const counts = new Map<string, number>();
  for (const scene of chapter.scenes) {
    for (const person of scene.participants) {
      if (person.role === "present" || person.role === "pov") {
        const id = person.characterId.split("@")[0] ?? person.characterId;
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
  }
  const named = new Map(chapter.characters.map((item) => [item.id, item]));
  return [...counts]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .flatMap(([id]) => {
      const person = named.get(id);
      return person === undefined || person.minor === true ? [] : [person.displayName];
    })
    .slice(0, TOP_CAST);
}

function monthLabel(yearMonth: string): string {
  const [year, month] = yearMonth.split("-").map(Number) as [number, number];
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
}

/** "October 1981 – July 1991", or one month when the story stays inside it. */
export function storySpanLabel(from: string | null, to: string | null): string | null {
  if (from === null || to === null) {
    return null;
  }
  return from === to ? monthLabel(from) : `${monthLabel(from)} – ${monthLabel(to)}`;
}

export function bookCoverFile(names: readonly string[]): string | undefined {
  return names.find((name) => name.startsWith(`${BOOK_COVER_STEM}.`));
}
